import { Card, CardHeader, Skeleton, Table } from "@helmr/ui";
import { OTP_CHANNELS } from "@helmr/contracts";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { getAttendance, getCommission, getInvoicing, getLeadsSettings, getMemberships, getPayroll, getSettings, listBranches,
  patchAttendance, patchCommission, patchInvoicing, patchLeadsSettings, patchMemberships, patchPayroll, patchSettings } from "../../api/settings";
import { getMessagingSettings, patchMessagingSettings } from "../../api/messaging";
import { getPtSchedule, patchPtSchedule } from "../../api/pt";
import { listAllItems } from "../../api/catalogue-admin";
import { Page, useScope } from "./kit";
import { SectionForm, type CustomProps, type FieldDef } from "./SectionForm";
import { RulesEditor, StaffPick, WeekHoursEditor } from "./editors";
import s from "./settings.module.css";
import { section } from "./sections";
import { t } from "./strings";

const num = (key: string, label: string, hint?: string, nullable?: boolean): FieldDef => ({ key, path: [key], label, hint, kind: "int", nullable });

export function BusinessPage() {
  const { ctx, qc } = useScope();
  const fields: FieldDef[] = [
    { key: "currency", path: ["currency"], label: "Currency", kind: "text", upper: true, hint: "ISO code, such as the one your bank account uses." },
    { key: "country", path: ["country"], label: "Country", kind: "text", upper: true, hint: "Two-letter ISO code." },
    { key: "timezone", path: ["timezone"], label: "Timezone", kind: "text", hint: "IANA name. Branches may set their own." },
    { key: "locale", path: ["locale"], label: "Number and date locale", kind: "text" },
    { key: "fyStartMonth", path: ["fyStartMonth"], label: "Financial year starts in month", kind: "int", hint: "1 to 12." },
    { key: "tax.system", path: ["tax", "system"], label: "Tax system label", kind: "text", hint: "Printed on invoices." },
    { key: "tax.defaultRateBp", path: ["tax", "defaultRateBp"], label: "Default tax rate (%)", kind: "percent", hint: "New catalogue items start here; each item can override it." },
    { key: "tax.pricesIncludeTax", path: ["tax", "pricesIncludeTax"], label: "Price entry", kind: "bool", hint: "New prices are entered tax-inclusive" },
    { key: "taxId", path: ["taxId"], label: "Tax ID", kind: "text", nullable: true, upper: true, hint: "GSTIN in India. Branches inherit it unless they set their own." },
    { key: "paymentAccountRef", path: ["paymentAccountRef"], label: "Payment account reference", kind: "text", nullable: true },
    { key: "sessionTtlDays", path: ["sessionTtlDays"], label: "Stay signed in (days)", kind: "int", hint: "30 to 90 on a trusted device." },
    { key: "otpChannels", path: ["otpChannels"], label: "Sign-in code channels", kind: "channels", full: true, options: OTP_CHANNELS.map((c) => ({ value: c, label: c.replace(/_/g, " ") })), hint: "Tried in the order you tick them." },
  ];
  return (
    <Page title={t.sections.business.title} description={t.sections.business.body} allow={section("business")}>
      <SectionForm title="Business settings" what="business settings" queryKey="business" load={getSettings} save={patchSettings} fields={fields} canEdit={ctx.has("settings.business_edit")}
        // Currency, locale and timezone are read from /me: amounts must not go on being parsed in the old currency.
        onSaved={() => void qc.invalidateQueries({ queryKey: ["me"] })} />
    </Page>
  );
}

export function InvoicingPage() {
  const { ctx } = useScope();
  const fields: FieldDef[] = [
    { key: "legalName", path: ["legalName"], label: "Legal name on tax documents", kind: "text", full: true, hint: "Needed before a registered seller can issue an invoice." },
    { key: "invoicePrefix", path: ["invoicePrefix"], label: "Invoice prefix", kind: "text" },
    { key: "billOfSupplyPrefix", path: ["billOfSupplyPrefix"], label: "Bill of supply prefix", kind: "text" },
    { key: "creditNotePrefix", path: ["creditNotePrefix"], label: "Credit note prefix", kind: "text", hint: "Must differ from the two above." },
    num("seqWidth", "Digits in the number", "Minimum digits in the sequence part."),
    num("maxNumberLength", "Longest document number"),
    { key: "numberPattern", path: ["numberPattern"], label: "Allowed characters", kind: "text", hint: "One character class, like ^[A-Za-z0-9/-]+$" },
  ];
  return (
    <Page title={t.sections.invoicing.title} description={t.sections.invoicing.body} allow={section("invoicing")}>
      <SectionForm title="Invoicing" what="invoicing settings" queryKey="invoicing" load={(c) => getInvoicing(c).then((r) => r.invoicing)} save={patchInvoicing} fields={fields} canEdit={ctx.has("settings.invoicing_edit")} />
      <SeriesCard />
    </Page>
  );
}

function SeriesCard() {
  const sc = useScope();
  const q = useQuery({ queryKey: sc.key("invoicing", "series"), queryFn: () => getInvoicing(sc.client).then((r) => r.series), retry: false });
  if (q.isPending || q.error || q.data.length === 0) return q.isPending ? <Card><CardHeader title="Numbering so far" /><div className={s.skel}><Skeleton height={34} /></div></Card> : null;
  return (
    <Card aria-label="Numbering so far">
      <CardHeader title="Numbering so far" subtitle="The last number issued in each series. Series cannot be edited." />
      <Table label="Invoice series" rows={q.data} rowKey={(r) => `${r.taxIdentity}|${r.fy}|${r.docType}`}
        columns={[{ key: "d", header: "Document", cell: (r) => r.docType }, { key: "fy", header: "Year", cell: (r) => r.fy }, { key: "id", header: "Tax ID", cell: (r) => r.taxIdentity }, { key: "p", header: "Prefix", cell: (r) => r.prefix }, { key: "n", header: "Last number", align: "end", cell: (r) => r.lastSeq }]} />
    </Card>
  );
}

function FeeItemPick({ value, onChange, disabled, id }: CustomProps) {
  const sc = useScope();
  const q = useQuery({ queryKey: sc.key("items", "all"), queryFn: () => listAllItems(sc.client), retry: false });
  const items = (q.data ?? []).filter((i) => i.kind === "fee" || i.kind === "service");
  return (
    <select id={id} className={s.select} value={(value as string | null) ?? ""} disabled={disabled} onChange={(e) => onChange(e.target.value || null)}>
      <option value="">No fee: freezes are free</option>
      {items.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
    </select>
  );
}

export function MembershipsPage() {
  const { ctx } = useScope();
  const fields: FieldDef[] = [
    num("maxFreezeDaysPerPlan", "Most frozen days per plan"),
    num("maxFreezesPerPlan", "Most freezes per plan"),
    num("minNoticeDays", "Notice before a freeze (days)", "0 lets a freeze start today."),
    { key: "feeItemId", path: ["feeItemId"], label: "Freeze fee item", kind: "custom", render: (p) => <FeeItemPick {...p} /> },
  ];
  return (
    <Page title={t.sections.memberships.title} description={t.sections.memberships.body} allow={section("memberships")}>
      <SectionForm title="Membership rules" what="membership settings" queryKey="memberships" load={getMemberships} save={patchMemberships} fields={fields} canEdit={ctx.has("settings.memberships_edit")} />
    </Page>
  );
}

export function CommissionPage() {
  const { ctx } = useScope();
  const sel = (key: string, label: string, options: Array<[string, string]>, hint: string): FieldDef => ({ key, path: [key], label, hint, kind: "select", options: options.map(([value, l]) => ({ value, label: l })) });
  const fields: FieldDef[] = [
    sel("unusedAtExpiry", "Sessions unused at expiry", [["no_earning", "Trainer earns nothing"], ["pay_remaining_to_assigned_trainer", "Pay the remaining to the assigned trainer"]], "What happens to a PT pack's unused sessions when it expires."),
    sel("noShowLateCancel", "No-show and late cancel", [["counts", "Counts as a delivered session"], ["does_not_count", "Does not count"]], "Whether the trainer earns for them."),
    sel("deliveredButUnpaid", "Delivered but unpaid", [["pay_anyway", "Pay anyway"], ["hold_until_paid", "Hold until paid"], ["pro_rata_paid", "Pay pro rata to what was paid"]], "Commission on a session the member has not fully paid for."),
    sel("heldAfterPartRefund", "After a part refund", [["recompute_on_effective_price", "Recompute on the effective price"], ["owner_releases_manually", "Owner releases manually"]], "What happens to held commission."),
  ];
  return (
    <Page title={t.sections.commission.title} description={t.sections.commission.body} allow={section("commission")}>
      <SectionForm title="Commission decisions" what="commission settings" queryKey="commission" load={getCommission} save={patchCommission} fields={fields} canEdit={ctx.has("settings.commission_edit")} />
    </Page>
  );
}

export function PayrollPage() {
  const { ctx } = useScope();
  const fields: FieldDef[] = [{ key: "contractorTdsBp", path: ["contractorTdsBp"], label: "Contractor TDS rate (%)", kind: "percent", nullable: true, hint: "Leave empty until confirmed with your CA: drafting a contractor's payroll is refused until it is set." }];
  return (
    <Page title={t.sections.payroll.title} description={t.sections.payroll.body} allow={section("payroll")}>
      <SectionForm title="Payroll" what="payroll settings" queryKey="payroll" load={getPayroll} save={patchPayroll} fields={fields} canEdit={ctx.has("settings.payroll_edit")} />
    </Page>
  );
}

export function AttendancePage() {
  const { ctx } = useScope();
  const fields: FieldDef[] = [
    num("maxShiftHours", "Longest shift (hours)", "An out within this many hours of an open in closes that shift."),
    num("debounceSeconds", "Ignore repeat punches within (seconds)"),
    num("fullDayMinutes", "Full day (worked minutes)"),
    num("halfDayMinutes", "Half day (worked minutes)", "Below this the day is absent."),
  ];
  return (
    <Page title={t.sections.attendance.title} description={t.sections.attendance.body} allow={section("attendance")}>
      <SectionForm title="Staff attendance" what="attendance settings" queryKey="attendance" load={getAttendance} save={patchAttendance} fields={fields} canEdit={ctx.has("settings.attendance_edit")} />
    </Page>
  );
}

export function PtSchedulePage() {
  const { ctx } = useScope();
  const fields: FieldDef[] = [
    { key: "defaultBookingStyle", path: ["defaultBookingStyle"], label: "How PT packs are booked", kind: "select", hint: "Used for any PT plan that does not choose its own. Change one plan under Plans and catalogue.",
      options: [{ value: "flexible", label: "Session by session" }, { value: "fixed_slot", label: "Fixed weekly slot (pick the days once)" }, { value: "daily", label: "Daily, Monday to Saturday" }] },
    { key: "autoMark", path: ["autoMark"], label: "Automatic marking", kind: "bool", hint: "Fixed-slot packs only: a member's check-in completes that day's session; a session with no check-in becomes a no-show when the day ends. Staff can still change or waive." },
  ];
  return (
    <Page title={t.sections.ptSchedule.title} description={t.sections.ptSchedule.body} allow={section("pt-schedule")}>
      <SectionForm title="PT booking" what="PT booking settings" queryKey="pt-schedule" load={getPtSchedule} save={patchPtSchedule} fields={fields} canEdit={ctx.has("pt.auto_mark_settings")} />
    </Page>
  );
}

export function LeadsSettingsPage() {
  const { ctx } = useScope();
  const branches = useBranchOptions();
  const fields: FieldDef[] = useMemo(() => [
    num("firstResponseMinutes", "First call due after (minutes)", "Counted from opening time if the lead arrives after hours."),
    num("dedupeWindowDays", "Merge repeat enquiries within (days)", "The same phone inside this window joins the existing lead."),
    { key: "defaultCountryCode", path: ["defaultCountryCode"], label: "Default country code", kind: "text", hint: "A number typed without one is read in this country, e.g. 91." },
    num("nationalNumberLength", "Digits in a national number"),
    { key: "defaultOwners", path: ["defaultOwners"], label: "Default owners (round robin)", kind: "custom", full: true, hint: "Used when no assignment rule matches.", render: (p) => <StaffPick {...p} /> },
    { key: "assignmentRules", path: ["assignmentRules"], label: "Assignment rules", kind: "custom", full: true, hint: "The first rule that matches wins.", render: (p) => <RulesEditor {...p} branches={branches} /> },
    { key: "businessHours", path: ["businessHours"], label: "Business hours for follow-up timing", kind: "custom", full: true, render: (p) => <WeekHoursEditor {...p} /> },
  ], [branches]);
  return (
    <Page title={t.sections.leads.title} description={t.sections.leads.body} allow={section("leads")}>
      <SectionForm title="Lead handling" what="lead settings" queryKey="leads" load={getLeadsSettings} save={patchLeadsSettings} fields={fields} canEdit={ctx.has("settings.leads_setup_edit")} />
    </Page>
  );
}

export function useBranchOptions() {
  const sc = useScope();
  const q = useQuery({ queryKey: sc.key("branches"), queryFn: () => listBranches(sc.client), retry: false });
  return useMemo(() => (q.data ?? []).map((b) => ({ id: b.id, name: b.name })), [q.data]);
}

export function MessagingSettingsForm() {
  const { ctx } = useScope();
  const fields: FieldDef[] = [
    { key: "paused", path: ["paused"], label: "Sending", kind: "bool", hint: "Paused: nothing is sent. Turn this off only when you are ready for members to receive messages." },
    { key: "templateLanguage", path: ["templateLanguage"], label: "Template language", kind: "text", hint: "A provider language code such as en." },
    { key: "fallbackLanguage", path: ["fallbackLanguage"], label: "Fallback language", kind: "text", nullable: true, hint: "Empty sends nothing when no approved template exists." },
    { key: "optOutKeywords", path: ["optOutKeywords"], label: "Opt-out words", kind: "list", hint: "Comma separated." },
    { key: "optInKeywords", path: ["optInKeywords"], label: "Opt-in words", kind: "list", hint: "Comma separated." },
    num("maxPerRecipientPerDay", "Most reminders per person per day"),
    num("maxLateHours", "Never send more than this many hours late"),
    num("logRetentionDays", "Keep the message log (days)"),
  ];
  return <SectionForm title="Messaging settings" what="messaging settings" queryKey="messaging" load={getMessagingSettings} save={patchMessagingSettings} fields={fields} canEdit={ctx.has("settings.messaging_edit")}
    note="Helmr sends nothing in this build: sends are a stub until a transport is approved. These settings are stored for when it is." />;
}

